import { Checkbox } from "@progress/kendo-react-inputs";
import React, { useEffect, useState } from "react";
import { getAliceRecommendedReferenceAttributeForRelation, getAliceRecommendedRelations } from "../../../../API/AliceGPT/AliceGPT";
import * as AliceGPTEvents from "../../../../Context/AliceGPT/AliceGPTContextEvents";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Loader from "../../../Shared/Loader/loader";
import SelectedCardinality from "../../../LinkedEntities/Field/SelectedCardinality";
import { USER_TO_USER_RELATION } from "./variables";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import useFeatureFlags from "../../../../Hooks/FeatureFlag/useFeatureFlags";
import { ATTRIBUTE_LABEL } from "../../../Helpers/Labels/AttributeLabel";
import { Tooltip } from '@progress/kendo-react-tooltip';
import RecommendedRelationCard from "../../../Shared/Recommendation/Relations/RecommendedRelationCard";
import FailedToLoadError from "../../../Shared/Recommendation/Error/FailedToLoadError";
import { useTranslation } from "react-i18next";
import { getEntitiesExceedingAttributeLimitOnCreatingRelations } from "./utils";
import { Dialog } from '@progress/kendo-react-dialogs';
import AddRelationDialog from "./AddRelationDialog";

function AliceRecommendedRelations({ isFromExtension, onDuplicateChange }) {
    const { t } = useTranslation([
        "create_project/alice_recommended_relations",
        "linked_entity/recommended_relation_card"
    ], { useSuspense: false });
    const [state, dispatch] = useAliceGPT();
    const featureFlags = useFeatureFlags();

    const [loading, setLoading] = useState(false);
    const [referenceAttributesLoaded, setReferenceAttributesLoaded] = useState(true);
    const [showAddRelationDialog, setShowAddRelationDialog] = useState(false);
    const [duplicateError, setDuplicateError] = useState(null);
    const [newRelation, setNewRelation] = useState({
        from: '',
        to: '',
        type: 'one-to-one',
        setReferenceAttribute: false
    });
    const [addRelationReferenceAttribute, setAddRelationReferenceAttribute] = useState('');
    const [addRelationError, setAddRelationError] = useState('');

    let numRelationsSelected = state.relations.filter(relation => relation.selected).length;

    // Filter out User Profile and prevent selecting the same entity in both fields
    const filteredEntities = state.entities.filter(e => !e.isUserEntity);
    const entityOptions = filteredEntities.filter(entity => entity.checked).map(entity => ({ text: entity.name, value: entity.name }));

    useEffect(() => {
        const selectedRelations = state.relations.filter(r => r.selected);
        const pairs = new Set();
        let duplicatePair = null;

        for (const rel of selectedRelations) {
            const pairKey = [rel.from, rel.to].sort().join('-');
            if (pairs.has(pairKey)) {
                duplicatePair = [rel.from, rel.to];
                break;
            }
            pairs.add(pairKey);
        }

        if (duplicatePair) {
            const error = t("errors.duplicate_relation_error", {
                entity1: duplicatePair[0],
                entity2: duplicatePair[1],
                defaultValue: `Multiple relations between ${duplicatePair[0]} and ${duplicatePair[1]} are selected. Please select only one.`
            });
            setDuplicateError(error); // local state for message
            onDuplicateChange(true); // notify parent
        } else {
            setDuplicateError(null);
            onDuplicateChange(false);
        }
    }, [state.relations, onDuplicateChange, t]);

    useEffect(() => {
        if (selectedEntitiesChanged()) {
            debugger;
            dispatch({
                type: AliceGPTEvents.UPDATE_PREVIOUS_SELECTED_ENTITY_NAME
            })
            // dispatch({
            //     type: AliceGPTEvents.TOGGLE_LOAD_EDIT_PAGE_CONFIGURATION
            // })
            dispatch({
                type: AliceGPTEvents.RESET_RELATIONS_LOADED
            })
            const entityNames = state.entities.filter(entity => entity.checked && !entity.isUserEntity).map(entity => entity.name);
            setLoading(true);
            getAliceRecommendedRelations(entityNames, state.form.description, state.form.model, isFromExtension)
                .then(response => {
                    // filter relation which has from and to as same entity and
                    const filteredResponse = response.filter(relation => relation.from !== relation.to)
                    // filter one relation for each pair of entity
                    const uniqueRelations = [];
                    const uniqueRelationKeys = {};
                    for (const relation of filteredResponse) {
                        const relationKey = `${relation.from}-${relation.to}`;
                        const reverseRelationKey = `${relation.to}-${relation.from}`;
                        if (!uniqueRelationKeys[relationKey] && !uniqueRelationKeys[reverseRelationKey]) {
                            uniqueRelationKeys[relationKey] = true;
                            uniqueRelations.push(relation);
                        }
                    }
                    const relations = uniqueRelations.map(relationToStateArrayElement);
                    dispatch({
                        type: AliceGPTEvents.RELATIONS_LOADED,
                        payload: relations
                    })
                    setReferenceAttributesLoaded(false);
                })
                .catch(error => {
                    dispatch({
                        type: AliceGPTEvents.FACED_ERROR,
                        payload: {
                            error: error.message
                        }
                    })
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            dispatch({
                type: AliceGPTEvents.RELATIONS_LOADED,
                payload: state.relations
            })
        }
    }, []);

    function selectedEntitiesChanged() {
        const selectedEntities = state.namesOfSelectedEntities;
        const previousSelectedEntities = state.previousNamesOfSelectedEntities;
        if (selectedEntities.length !== previousSelectedEntities.length) {
            state.relations.forEach(relation => relation.selected = false);
            return true;
        }
        for (const selectedEntity of selectedEntities) {
            if (!previousSelectedEntities.includes(selectedEntity)) {
                return true;
            }
        }
        return false;
    }

    function relationToStateArrayElement(relation) {
        return {
            from: relation.from || relation[0],
            to: relation.to || relation[1],
            cardinality: relation.cardinality || relation[2],
            showReferenceAttribute: relation.cardinality === "1-*" && doesFromEntityHaveValidAttributes(relation),
            referenceAttribute: "",
            checked: false,
            selected: true
        }
    }

    useEffect(() => {
        if (!loading && !state.initialAttributesLoaded) {
            loadReferenceAttributesForRelations();
        }
    }, [state.initialAttributesLoaded])

    // Auto-clear swap errors after 5 seconds
    useEffect(() => {
        const timeouts = [];
        
        Object.keys(state.swapErrors).forEach(relationIndex => {
            const timeout = setTimeout(() => {
                dispatch({
                    type: AliceGPTEvents.CLEAR_SWAP_ERROR,
                    payload: { relationIndex: parseInt(relationIndex) }
                });
            }, 5000);
            timeouts.push(timeout);
        });

        // Cleanup timeouts on unmount or when swapErrors change
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, [state.swapErrors, dispatch]);

    async function loadReferenceAttributesForRelations() {
        const results = [];
        state.relations.forEach((relation, index) => {
            const fromEntityHasValidAttributes = doesFromEntityHaveValidAttributes(relation);
            if (
                relation.cardinality === "1-*" &&
                fromEntityHasValidAttributes &&
                !relation.isStatic
            ) {
                const response = getRecommendedAttribute(index);
                results.push(response);
            }
        });
        for (const response of results) {
            dispatch({
                type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
                payload: { relationIndex: response.relationIndex, referenceAttribute: response.attributeName }
            })
        }
        dispatch({
            type: AliceGPTEvents.INITIAL_REFERENCE_ATTRIBUTES_LOADED
        })
    }

    function doesFromEntityHaveValidAttributes(relation) {
        const fromEntity = state.entities.find(entity => entity.name === relation.from);
        const fromEntityAttributes = fromEntity.attributes.filter(attribute => attribute.selected && attribute.type === "string");
        return fromEntityAttributes.length > 0;
    }
    
    function toggleCheckbox(relationIndex) {
        if (state.relations[relationIndex].selected) {
            dispatch({
                type: AliceGPTEvents.RELATION_DESELECTED,
                payload: { relationIndex }
            });
        } else {
            dispatch({
                type: AliceGPTEvents.RELATION_SELECTED,
                payload: { relationIndex }
            });
        }
    }

    function toggleShowReferenceAttribute(relationIndex) {
        if (!state.relations[relationIndex].referenceAttribute) {
            const attribute = getRecommendedAttribute(relationIndex)
            if (attribute.attributeName) {
                dispatch({
                    type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
                    payload: {
                        relationIndex, referenceAttribute: attribute.attributeName
                    }
                });
            } else {
                dispatch({
                    type: AliceGPTEvents.SHOW_REFERENCE_ATTRIBUTE_TOGGLED,
                    payload: { relationIndex }
                })
            }
        }
        dispatch({
            type: AliceGPTEvents.SHOW_REFERENCE_ATTRIBUTE_TOGGLED,
            payload: { relationIndex }
        });
    }

    function getRecommendedAttribute(relationIndex) {
        const relation = state.relations[relationIndex];
        const fromEntity = state.entities.find(entity => entity.name === relation.from);
        const selectedFromEntityAttributes = fromEntity.attributes.filter(attribute => attribute.selected && attribute.type === "String");

        if (selectedFromEntityAttributes.length === 0) {
            return { relationIndex, attributeName: null };
        }

        return { relationIndex, attributeName: selectedFromEntityAttributes[0].name };
    }

    function getSelectedAttributeNameOfEntity(entityName) {
        const entity = state.entities.find(entity => entity.name === entityName);
        const selectedTextAttributes = entity.attributes.filter(attribute => attribute.selected && attribute.type === "String");
        return selectedTextAttributes.map(attribute => attribute.name);
    }

    function handleReferenceAttributeChange(event, relationIndex) {
        dispatch({
            type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
            payload: { referenceAttribute: event.target.value, relationIndex }
        });
    }

    function handleCardinalityChange(event, relationIndex) {
        dispatch({
            type: AliceGPTEvents.CARDINALITY_CHANGED,
            payload: { cardinality: event.target.value, relationIndex }
        });
    }

    function handleSwap(event, relationIndex) {
        event.stopPropagation();
        dispatch({
            type: AliceGPTEvents.RELATION_SWAP,
            payload: { relationIndex }
        });
    }

    function getIsAttributeLimitExceeded() {
        const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
            state.entities,
            state.relations,
            state.subscription
        )
        if (entitiesWithAttributeLimitExceeded.length > 0) {
            return (
                <span className="text-danger">
                    {t("max_attributes_exceeded", {
                        limit: state.subscription.maxAttributesPerApp,
                        entitiesWithAttributeLimitExceeded: entitiesWithAttributeLimitExceeded.join(", ")
                    })}
                </span>
            )
        }
        return <></>
    }

    function handleOpenAddRelationDialog() {
        setNewRelation({ from: '', to: '', type: 'one-to-one', setReferenceAttribute: false });
        setAddRelationReferenceAttribute('');
        setAddRelationError('');
        setShowAddRelationDialog(true);
    }
    function handleCloseAddRelationDialog() {
        setShowAddRelationDialog(false);
        setAddRelationError('');
    }

    function handleNewRelationChange(field, value) {
        let updated = { ...newRelation, [field]: value };
        setNewRelation(updated);
        // Only live contradictive relation validation here
        let error = '';
        const cardinality = updated.type === 'one-to-one' ? '1-1' : updated.type === 'one-to-many' ? '1-*' : '*-*';
        if (cardinality === '1-*') {
            const contradictive = state.relations.some(rel =>
                rel.from === updated.to &&
                rel.to === updated.from
            );
            if (contradictive) {
                error = t('create_project.alice_recommended_relations.error.contradictive_relation', 'Contradictive relation: a relation already exists in the opposite direction.');
            }
        }
        setAddRelationError(error);
    }

    // Helper to get string attributes for the selected 'from' entity in Add Relation Dialog
    function getReferenceAttributesForAddDialog() {
        const fromEntity = filteredEntities.find(entity => entity.name === newRelation.from);
        if (!fromEntity) return [];
        return fromEntity.attributes.filter(attr => attr.type === 'String').map(attr => attr.name);
    }

    // Handler for reference attribute change in Add Relation Dialog
    function handleAddRelationReferenceAttributeChange(attrName) {
        setAddRelationReferenceAttribute(attrName);
    }

    // Update reference attribute when 'from' entity changes
    useEffect(() => {
        setAddRelationReferenceAttribute('');
    }, [newRelation.from, newRelation.setReferenceAttribute]);

    function handleAddNewRelation() {
        if (!newRelation.from || !newRelation.to || !newRelation.type) {
            setAddRelationError(t('create_project.alice_recommended_relations.error.missing_fields', 'Please fill all required fields.'));
            return;
        }
        if (newRelation.from === newRelation.to) {
            setAddRelationError('Cannot link an object to itself.');
            return;
        }
        if (newRelation.setReferenceAttribute && !addRelationReferenceAttribute) {
            setAddRelationError(t('create_project.alice_recommended_relations.error.missing_reference_attribute', 'Please select a reference attribute.'));
            return;
        }
        // Check for duplicate relation (regardless of selected or not)
        const cardinality = newRelation.type === 'one-to-one' ? '1-1' : newRelation.type === 'one-to-many' ? '1-*' : '*-*';
        const duplicate = state.relations.some(rel =>
            rel.from === newRelation.from &&
            rel.to === newRelation.to &&
            rel.cardinality === cardinality
        );
        if (duplicate) {
            setAddRelationError('Relation already exists!');
            return;
        }
        if (cardinality === '1-*') {
            const contradictive = state.relations.some(rel =>
                rel.from === newRelation.to &&
                rel.to === newRelation.from
            );
            if (contradictive) {
                setAddRelationError(t('create_project.alice_recommended_relations.error.contradictive_relation', 'Contradictive relation: a relation already exists in the opposite direction.'));
                return;
            }
        }
        dispatch({
            type: AliceGPTEvents.ADD_RELATION,
            payload: {
                from: newRelation.from,
                to: newRelation.to,
                cardinality,
                showReferenceAttribute: newRelation.setReferenceAttribute,
                referenceAttribute: newRelation.setReferenceAttribute ? addRelationReferenceAttribute : '',
                checked: false,
                selected: true
            }
        });
        setShowAddRelationDialog(false);
        setNewRelation({ from: '', to: '', type: 'one-to-one', setReferenceAttribute: false });
        setAddRelationReferenceAttribute('');
        setAddRelationError('');
    }

    if (loading) {
        return (
            <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                <img src={getDatalakeImageUrl("loaders/RecommendationLoader.gif")} alt=""/>
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        );
    }

    if (state.error) {
        return (
            <FailedToLoadError />
        )
    }

    return (
        <>
            {/* Header Section */}
            <div className="d-flex align-items-center justify-content-between p-3 bg-1" style={{borderRadius: '12px 12px 0 0'}}>
                <div className="d-flex align-items-center gap-2">
                    <Checkbox
                        checked={state.relations.every(relation => relation.selected)}
                        value={state.relations.every(relation => relation.selected)}
                        onChange={() => dispatch({ type: AliceGPTEvents.TOGGLE_SELECT_ALL_RELATIONS })}
                        labelPlacement="left"
                    />
                    <span className="font-size-4 font-weight-600 text-1">
                        { t("select_all.label") }
                    </span>
                    <i
                        className="fa-solid fa-circle-info text-2 font-size-3"
                        style={{ opacity: 0.5 }}
                        title={t("select_all.tooltip")}
                    />
                </div>
                <div>
                    {
                        duplicateError &&
                        <span className="text-danger ms-2">
                            {duplicateError}
                        </span>
                    }
                    {
                        numRelationsSelected > state.subscription.maxRelationsPerApp &&
                        <span className="text-danger ms-2">
                            {t("max_relations_exceeded", { maxRelations: state.subscription.maxRelationsPerApp })}
                        </span>
                    }
                    &nbsp;&nbsp;
                    {getIsAttributeLimitExceeded()}
                    <button
                        className="btn btn-primary font-size-2 px-4 py-2"
                        style={{ marginLeft: 'auto' }}
                        onClick={handleOpenAddRelationDialog}
                        type="button"
                    >
                        {t('add_new_relation_button_text', '+ New Relation')}
                    </button>
                </div>
            </div>
            {showAddRelationDialog && (
                <AddRelationDialog
                    onClose={handleCloseAddRelationDialog}
                    onAdd={handleAddNewRelation}
                    newRelation={newRelation}
                    relations={state.relations}
                    onChange={handleNewRelationChange}
                    entityOptions={entityOptions}
                    referenceAttributes={getReferenceAttributesForAddDialog()}
                    referenceAttribute={addRelationReferenceAttribute}
                    onReferenceAttributeChange={handleAddRelationReferenceAttributeChange}
                    error={addRelationError}
                    maxRelations={state.subscription.maxRelationsPerApp}
                    t={t}
                />
            )}
            {/* Relation Cards Grid */}
            <div className="row gy-3 gx-2">
                {
                    state.relations.length === 0 &&
                    <div className="col-12">
                        <div className="alert alert-relation">
                            {t("create_project.alice_recommended_relations.no_relations_found")}
                        </div>
                    </div>
                }
                {state.relations.map((relation, index) => (
                    <div className="col-12 col-md-6 col-lg-4" key={index}>
                        <RecommendedRelationCard
                            fromEntityName={relation.from}
                            toEntityName={relation.to}
                            cardinality={relation.cardinality}
                            addReferenceAttribute={relation.showReferenceAttribute}
                            referenceAttribute={relation.referenceAttribute}
                            referenceAttributes={getSelectedAttributeNameOfEntity(relation.from)}
                            selected={relation.selected}
                            onCardinalityChange={(event) => handleCardinalityChange(event, index)}
                            onAddReferenceAttributeChange={() => toggleShowReferenceAttribute(index)}
                            onReferenceAttributeChange={(event) => handleReferenceAttributeChange(event, index)}
                            onToggle={() => toggleCheckbox(index)}
                            onSwap={(e) => handleSwap(e, index)}
                            error={state.swapErrors[index] ? t('errors.swap_error') : null}
                        />
                    </div>
                ))}
            </div>
        </>
    );
}

export default AliceRecommendedRelations;